import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { stockAPI } from "../../lib/api";
import { Package, Plus, Edit, BookOpen, AlertTriangle, X, Search } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const fmtBRL = v => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const EMPTY_PRODUCT = { name: "", category_id: "", sku: "", isbn: "", edition: "", description: "", price: "", cost: "", stock_min: 50, unit: "un", active: true };
const EMPTY_CAT = { name: "", description: "" };

function CategoryModal({ open, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY_CAT);
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!form.name) { toast.error("Nome obrigatório"); return; }
        setSaving(true);
        try { await stockAPI.createCategory(form); toast.success("Categoria criada"); onSaved(); onClose(); }
        catch { toast.error("Erro"); } finally { setSaving(false); }
    };
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-sm shadow-2xl">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between">
                    <h2 className="font-bold text-stone-100">Nova Categoria</h2>
                    <button onClick={onClose}><X className="w-4 h-4 text-stone-500" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                        placeholder="Nome da categoria" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-stone-400">Cancelar</button>
                    <button onClick={save} disabled={saving} className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50">
                        {saving ? "Salvando..." : "Criar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProductModal({ open, onClose, onSaved, editing, categories }) {
    const [form, setForm] = useState(EMPTY_PRODUCT);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(editing ? { ...editing } : { ...EMPTY_PRODUCT, category_id: categories[0]?.id || "" }); }, [editing, open]);

    const save = async () => {
        if (!form.name) { toast.error("Nome obrigatório"); return; }
        setSaving(true);
        try {
            if (editing) await stockAPI.updateProduct(editing.id, form);
            else         await stockAPI.createProduct(form);
            toast.success(editing ? "Produto atualizado" : "Produto criado");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between flex-shrink-0">
                    <h2 className="font-bold text-stone-100">{editing ? "Editar" : "Novo"} Produto</h2>
                    <button onClick={onClose}><X className="w-4 h-4 text-stone-500" /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Nome *</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="ex: Como se Tornar uma Mulher com Propósito" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Categoria</label>
                            <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.category_id} onChange={e => setForm(f => ({...f, category_id: e.target.value}))}>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">SKU</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.sku} onChange={e => setForm(f => ({...f, sku: e.target.value}))} placeholder="LIVRO-001" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">ISBN</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.isbn} onChange={e => setForm(f => ({...f, isbn: e.target.value}))} placeholder="978-..." />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Edição</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.edition} onChange={e => setForm(f => ({...f, edition: e.target.value}))} placeholder="1ª edição" />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Preço venda (R$)</label>
                            <input type="number" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Custo (R$)</label>
                            <input type="number" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.cost} onChange={e => setForm(f => ({...f, cost: e.target.value}))} />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Mínimo alerta</label>
                            <input type="number" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.stock_min} onChange={e => setForm(f => ({...f, stock_min: Number(e.target.value)}))} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Descrição</label>
                        <textarea rows={2} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({...f, active: e.target.checked}))} /> Produto ativo
                    </label>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2 flex-shrink-0">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-stone-400">Cancelar</button>
                    <button onClick={save} disabled={saving} className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function StockProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [productModal, setProductModal] = useState(false);
    const [catModal, setCatModal] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [p, c] = await Promise.all([stockAPI.products(), stockAPI.categories()]);
            setProducts(p.data || []);
            setCategories(c.data || []);
        } catch { toast.error("Erro ao carregar"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const filtered = products.filter(p =>
        !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.isbn || "").includes(search)
    );

    return (
        <>
        <Header title="Produtos" subtitle={`${products.length} produto(s) cadastrado(s)`} actions={<><button onClick={() => setCatModal(true)} className="border border-stone-700 text-stone-300 hover:text-stone-100 text-sm px-3 py-2 rounded-lg transition-colors">+ Categoria</button><button onClick={() => setProductModal(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Novo Produto</button></>} />
        <div className="p-6 space-y-5">
            <ProductModal open={productModal} onClose={() => { setProductModal(false); setEditing(null); }}
                onSaved={load} editing={editing} categories={categories} />
            <CategoryModal open={catModal} onClose={() => setCatModal(false)} onSaved={load} />


            <div className="flex items-center gap-2 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-stone-500" />
                <input className="bg-transparent text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none flex-1"
                    placeholder="Buscar por nome ou ISBN..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>

            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : filtered.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <Package className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhum produto encontrado.</p>
                </div>
            ) : (
                <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-800">
                                <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Produto</th>
                                <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">ISBN / Edição</th>
                                <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Preço</th>
                                <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Estoque</th>
                                <th className="text-right px-4 py-3 text-xs text-stone-500 font-medium">Mínimo</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800/50">
                            {filtered.map(p => {
                                const low = p.stock_current <= p.stock_min;
                                return (
                                    <tr key={p.id} className="hover:bg-stone-800/30 transition-colors group">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${low ? "bg-red-900/30" : "bg-amber-900/20"}`}>
                                                    <BookOpen className={`w-4 h-4 ${low ? "text-red-400" : "text-amber-400"}`} />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-stone-200">{p.name}</p>
                                                    {p.sku && <p className="text-xs text-stone-500">{p.sku}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-stone-400">
                                            {p.isbn && <p>{p.isbn}</p>}
                                            {p.edition && <p>{p.edition}</p>}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm text-stone-300">{p.price ? fmtBRL(p.price) : "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`text-sm font-bold ${low ? "text-red-400" : "text-stone-100"}`}>{p.stock_current}</span>
                                            {low && <AlertTriangle className="w-3 h-3 text-orange-400 inline ml-1" />}
                                        </td>
                                        <td className="px-4 py-3 text-right text-xs text-stone-500">{p.stock_min}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                                                <Link to={`/estoque/produtos/${p.id}`}
                                                    className="p-1.5 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-700 transition-all text-xs">
                                                    Ver
                                                </Link>
                                                <button onClick={() => { setEditing(p); setProductModal(true); }}
                                                    className="p-1.5 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-700 transition-all">
                                                    <Edit className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
        </>
    );
}
